import type { ReactNode } from 'react'
import { TrendingUp, TrendingDown } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * Figma (node 17085:178971 — _Chart / Card):
 *
 * Container: bg-card, border border-border, rounded-lg (10px), shadow-xs, overflow-clip
 * Content: p-6, gap-6
 * Header: title (text-base font-semibold tracking-tight) + description (text-sm text-muted-foreground)
 * Footer: trending text (text-sm font-medium) + icon + date range (text-sm text-muted-foreground)
 */

export interface IntuiChartCardProps {
  /** Chart title */
  title: string
  /** Chart description */
  description?: string
  /** Trending text (e.g. "Trending up by 5.2% this month") */
  trendText?: string
  /** Trend direction for icon */
  trendDirection?: 'up' | 'down'
  /** Date range text (e.g. "January - June 2024") */
  dateRange?: string
  /** The chart content */
  children: ReactNode
  /** Additional className */
  className?: string
}

function IntuiChartCard({
  title,
  description,
  trendText,
  trendDirection = 'up',
  dateRange,
  children,
  className,
}: IntuiChartCardProps) {
  const TrendIcon = trendDirection === 'up' ? TrendingUp : TrendingDown

  return (
    <div
      className={cn(
        'flex flex-col overflow-clip rounded-lg border bg-card shadow-xs',
        className,
      )}
    >
      {/* Content */}
      <div className="flex flex-col gap-6 p-6">
        {/* Header */}
        <div className="flex flex-col gap-1.5">
          <p className="text-base font-semibold tracking-tight text-card-foreground">
            {title}
          </p>
          {description && (
            <p className="text-sm text-muted-foreground">{description}</p>
          )}
        </div>

        {/* Chart */}
        {children}

        {/* Footer */}
        {(trendText || dateRange) && (
          <div className="flex flex-col gap-2">
            {trendText && (
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium leading-none text-card-foreground">
                  {trendText}
                </p>
                <TrendIcon className="size-4" />
              </div>
            )}
            {dateRange && (
              <p className="text-sm leading-none text-muted-foreground">
                {dateRange}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
IntuiChartCard.displayName = 'IntuiChartCard'

export { IntuiChartCard }
